// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  NameValueList,
  NameValuePair,
  Page,
  PageContent,
  PageHeader
} from 'grommet'
import { Copy, FormDown, Previous } from 'grommet-icons'
import dayjs from 'dayjs'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { Layout } from '../../../../commoncomponents/layout/Layout'
import {
  ActionButton,
  Anchor,
  Button,
  DataTable,
  Loader,
  Notification,
  Tooltip,
  Typography
} from '../../../../components'
import { get, patch } from '../../../../utils/api-utils'
import { displayApiError } from '../../../../utils/error-handling-utils'
import { useVisibilityContext } from '../../../../context/visibility-context'
import Markdown from '../../../../commoncomponents/Markdown'

import { EditSCIMIntegrationModal } from './EditSCIMIntegrationModal'
import DeleteScimIntegerationModal from './DeleteScimIntegerationModal'
import ReplaceCredentailsModal from './ReplaceCredentailsModal'

const ExternalSCIMDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(['common', 'iam'])
  const { v2RbacPolicies } = useVisibilityContext()
  const LDFlags = useFlags()
  const scimAPIEndpoint = LDFlags['glcp-scim-api-endpoints']
  const APIEndpoint = `${scimAPIEndpoint}/identity/v2alpha1/scim/v2`

  const [loading, setLoading] = useState(false)
  const [apiErrorNotification, setApiErrorNotification] = useState(null)
  const [scimData, setScimData] = useState({})
  const [scimCredentialsList, setScimCredentialsList] = useState([])

  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showReplaceCredentailsModal, setShowReplaceCredentailsModal] =
    useState(false)

  const v2ExternalScimUpdatePerm = v2RbacPolicies?.includes(
    'identity.scim-integration.update'
  )

  const scimDataTempI18NKeys = {
    id: t('iam:external_scim_integrations.integration_id'),
    domains: t('iam:domains.domain_single'),
    updatedAt: t('iam:external_scim_integrations.last_used'),
    createdAt: t('iam:sso_profiles.view_edit_create.created_at'),
    // createdAt: t('iam:created_at'),
    createdBy: t('iam:external_scim_integrations.created_by')
  }

  const copyButtonFn = ({ hover, focus }) => (
    <Tooltip
      dropProps={{ align: { left: 'right' } }}
      info={
        <>
          {hover && !focus ? t('authz:copy') : null}
          {hover && focus ? t('authz:copied') : null}
        </>
      }
      testId="copy-tooltip"
    >
      <Copy size="medium" />
    </Tooltip>
  )

  const getFormatedEndPoint = (endpoint) => (
    <Box direction="row" align="center">
      <Box direction="column">
        <Typography type="text" truncate data-testid="scim-endpoint-title">
          <Tooltip
            // dropProps={{ align: { left: 'right' } }}
            info={endpoint}
            testId="scim-endpoint-title-tooltip"
          >
            {endpoint}
          </Tooltip>
        </Typography>
      </Box>
      <Box direction="column">
        <Button
          default
          onClick={() => navigator.clipboard.writeText(endpoint)}
          hoverIndicator="none"
          testId="recovery-account-email-copy-btn"
        >
          {copyButtonFn}
        </Button>
      </Box>
    </Box>
  )

  const getDateTimeFormat = (date) =>
    `${dayjs(date).format('MMMM DD, YYYY')} at ${dayjs(date).format('h:mm A')}`

  const fetchSCIMData = useCallback(() => {
    setLoading(true)

    get(`/internal-identity/v2alpha1/scim-integrations/${id}`, {}).then(
      (response) => {
        if (response?.data) {
          setScimData({
            ...response?.data,
            createdAt: getDateTimeFormat(response?.data?.createdAt),
            updatedAt: getDateTimeFormat(response?.data?.updatedAt)
          })
        }
        setLoading(false)
      },
      (error) => {
        setLoading(false)
        setApiErrorNotification(
          displayApiError(error, t, setApiErrorNotification)
        )
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const fetchSCIMCredentials = useCallback(() => {
    setLoading(true)

    get(
      `/internal-identity/v2alpha1/scim-integrations/${id}/scim-credentials`,
      {}
    ).then(
      (scimCredentials) => {
        if (scimCredentials?.data?.items) {
          setScimCredentialsList(scimCredentials?.data?.items)
        }

        setLoading(false)
      },
      (error) => {
        setLoading(false)
        setApiErrorNotification(
          displayApiError(error, t, setApiErrorNotification)
        )
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    fetchSCIMData()
  }, [fetchSCIMData])

  useEffect(() => {
    fetchSCIMCredentials()
  }, [fetchSCIMCredentials])

  const handleEditScimIntegration = (value) => {
    patch(`/internal-identity/v2alpha1/scim-integrations/${id}`, [
      {
        op: 'replace',
        value,
        path: '/name'
      }
    ]).then(() => {
      setShowEditModal(false)
      fetchSCIMData()
    })
  }

  const pageActions = () => {
    const edit = {
      label: t('common:edit'),
      testId: 'edit-external-scim-btn',
      onClick: () => setShowEditModal(true),
      hidden: !v2ExternalScimUpdatePerm
    }
    const del = {
      label: t('common:delete'),
      onClick: () => setShowDeleteModal(true),
      testId: 'remove-external-scim-btn',
      hidden: !v2RbacPolicies?.includes('identity.scim-integration.delete')
    }
    const replaceCredentails = {
      label: t('iam:external_scim_integrations.replace_credentails'),
      onClick: () => setShowReplaceCredentailsModal(true),
      testId: 'replace-credentails-external-scim-btn',
      hidden: !v2ExternalScimUpdatePerm
    }
    const actions = [edit]
    if (scimCredentialsList.length) {
      actions.push(replaceCredentails)
    }
    actions.push(del)
    return actions
  }

  const columns = [
    {
      property: 'scimKeyLastFour',
      header: t('iam:external_scim_integrations.token'),
      render: (data) => {
        const scimKey = data?.scimKeyLastFour
        return (
          <Typography type="text" size="medium" emphasis>
            <Markdown type="heading">
              {t('iam:external_scim_integrations.ending_in', { scimKey })}
            </Markdown>
          </Typography>
        )
      }
    },
    {
      property: 'createdAt',
      header: t('iam:sso_profiles.view_edit_create.created_at')
    },
    {
      property: 'age',
      header: t('iam:external_scim_integrations.age_days')
    },
    ...(v2ExternalScimUpdatePerm
      ? [
          {
            property: 'actions',
            header: '',
            render: () => (
              <ActionButton
                showOneActionAsDropDown
                testId="scim-credentails-action-button"
                actions={[
                  {
                    label: t(
                      'iam:external_scim_integrations.replace_credentails'
                    ),
                    onClick: () => setShowReplaceCredentailsModal(true)
                  }
                ]}
              />
            )
          }
        ]
      : [])
  ]

  const renderSCIMCredentailsTable = () => {
    return (
      <Box>
        {scimCredentialsList?.length > 0 && (
          <Box gap="smalll" margin={{ top: 'medium' }}>
            <Typography
              level="2"
              type="heading"
              testId="external-scim-details-page-title"
            >
              {t('iam:external_scim_integrations.credentials_title')}
            </Typography>
            {scimCredentialsList[0]?.age > 90 && (
              <Notification
                margin="medium"
                type="inline"
                text={
                  <Typography
                    type="text"
                    size="medium"
                    testId="crential-age-notification"
                  >
                    <Markdown>
                      {t(
                        'iam:external_scim_integrations.crential_age_notification',
                        {
                          token: scimCredentialsList[0]?.scimKeyLastFour
                        }
                      )}
                    </Markdown>
                  </Typography>
                }
                status="warning"
                testId="threshold-credentails-warning"
              />
            )}
            <DataTable
              grid={{
                columns,
                data: scimCredentialsList
              }}
              loading={loading}
              testId="scim-credentials-datatable"
            />
          </Box>
        )}
      </Box>
    )
  }

  const handleBackBtnClick = () =>
    navigate(`/manage-account/organization/external-scim`)

  return (
    <Box
      pad={{ horizontal: 'xlarge', bottom: 'large' }}
      width="xxlarge"
      alignSelf="center"
    >
      <Page>
        <PageContent>
          {loading ? (
            <Box direction="row" align="center" justify="center" pad="medium">
              <Loader testId="saml-view-loader" />
            </Box>
          ) : (
            <>
              <PageHeader
                responsive
                parent={
                  <Anchor
                    icon={<Previous />}
                    href="#"
                    label={t(
                      'iam:external_scim_integrations.external_scim_integrations_title'
                    )}
                    onClick={handleBackBtnClick}
                    data-testid="back-btn"
                  />
                }
                title={
                  <Typography
                    level="1"
                    type="heading"
                    testId="external-scim-details-page-title"
                  >
                    {scimData?.name}
                  </Typography>
                }
                actions={
                  <Box direction="row" align="center" gap="medium">
                    {pageActions()?.length > 0 && (
                      <ActionButton
                        label={t('common:actions')}
                        reverse
                        actions={pageActions()}
                        icon={<FormDown />}
                        testId="scim-integrations-details-action-btn"
                        showOneActionAsDropDown
                      />
                    )}
                  </Box>
                }
                data-testid="heading-page-title"
              />
              <Box gap="small" width="large">
                {scimData && (
                  <Box gap="small">
                    <NameValueList>
                      <NameValuePair
                        name={t(
                          'iam:external_scim_integrations.scim_api_endpoint'
                        )}
                      >
                        {getFormatedEndPoint(APIEndpoint)}
                      </NameValuePair>
                    </NameValueList>
                    {Object.entries(scimDataTempI18NKeys).map(
                      ([key, value]) => (
                        <NameValueList key={key}>
                          <NameValuePair name={value}>
                            <Typography type="text">{scimData[key]}</Typography>
                          </NameValuePair>
                        </NameValueList>
                      )
                    )}
                  </Box>
                )}
                {renderSCIMCredentailsTable()}
              </Box>
            </>
          )}
          {showEditModal && (
            <EditSCIMIntegrationModal
              setShowEditModal={setShowEditModal}
              handleEditScimIntegration={handleEditScimIntegration}
              scimName={scimData?.name}
            />
          )}
          {showDeleteModal && (
            <DeleteScimIntegerationModal
              setShowDeleteModal={setShowDeleteModal}
              scimId={id}
              scimName={scimData?.name}
            />
          )}
          {showReplaceCredentailsModal && (
            <ReplaceCredentailsModal
              setShowReplaceCredentailsModal={setShowReplaceCredentailsModal}
              scimId={id}
              scimName={scimData?.name}
              scimCredentialId={scimCredentialsList[0]?.id}
              onSuccess={fetchSCIMCredentials}
            />
          )}
          {apiErrorNotification}
        </PageContent>
      </Page>
    </Box>
  )
}

const ExternalSCIMDetailsPage = () => {
  return (
    <Layout>
      <ExternalSCIMDetails />
    </Layout>
  )
}
export default ExternalSCIMDetailsPage
